export type TemplateType = 'JOB_CARD' | 'THIRD_PARTY_DISPATCH';
export type Orientation = 'portrait' | 'landscape';
export type ElementType = 'field' | 'barcode' | 'qrcode' | 'image' | 'table' | 'barcode-table' | 'divider' | 'header' | 'text';
export type FontWeight = 'normal' | 'bold';
export type FontStyle = 'normal' | 'italic';
export type TextAlign = 'left' | 'center' | 'right';

export interface TemplateElement {
  id: string;
  tagKey: string;
  label: string;
  elementType: ElementType;
  /** Free-text content — only used when elementType === 'text' */
  content?: string;
  x: number;
  y: number;
  width: number;
  height: number;
  showLabel: boolean;
  fontSize: number;
  fontWeight: FontWeight;
  fontStyle: FontStyle;
  textAlign: TextAlign;
  color: string;
  backgroundColor: string;
  zIndex: number;
  /**
   * Column width percentages for table / barcode-table elements.
   * Array length must match the number of columns; values sum to 100.
   * When absent, all columns are equally wide.
   */
  columnWidths?: number[];
  /**
   * Per-column text alignment for table / barcode-table elements.
   * Array length must match the number of columns.
   * When absent, per-tag defaults apply (see columnAlignmentsForTable getter).
   */
  columnAlignments?: TextAlign[];
}

export interface ReceiptTemplate {
  id: string;
  name: string;
  type: TemplateType;
  isGlobalDefault: boolean;
  /** When false the template is a draft and will not be offered for printing. */
  isActive: boolean;
  widthMm: number;
  heightMm: number;
  orientation: Orientation;
  elements: TemplateElement[];
  createdAt: string;
  updatedAt: string;
  requiredByDate?: string;
}

export interface PageSizePreset {
  /** Stable English identifier — used as dataKey for dropdown comparison. */
  name: string;
  /** i18n key — resolved at render time via the translate pipe. */
  translateKey: string;
  widthMm: number | null;
  heightMm: number | null;
}

export const PAGE_SIZE_PRESETS: PageSizePreset[] = [
  { name: 'A4 Portrait',          translateKey: 'bikeshop.receipt_template.print.a4.potrait',    widthMm: 210,  heightMm: 297  },
  { name: 'A4 Landscape',         translateKey: 'bikeshop.receipt_template.print.a4.landscape',  widthMm: 297,  heightMm: 210  },
  { name: 'A5 Portrait',          translateKey: 'bikeshop.receipt_template.print.a5.potrait',    widthMm: 148,  heightMm: 210  },
  { name: 'A5 Landscape',         translateKey: 'bikeshop.receipt_template.print.a5.landscape',  widthMm: 210,  heightMm: 148  },
  { name: 'Label Small (50×25)',   translateKey: 'bikeshop.receipt_template.print.label.small',   widthMm: 50,   heightMm: 25   },
  { name: 'Label Medium (100×50)', translateKey: 'bikeshop.receipt_template.print.label.medium',  widthMm: 100,  heightMm: 50   },
  { name: 'Label Large (100×150)', translateKey: 'bikeshop.receipt_template.print.label.large',   widthMm: 100,  heightMm: 150  },
  { name: 'Custom',               translateKey: 'bikeshop.receipt_template.print.custom',         widthMm: null, heightMm: null },
];

export function createDefaultElement(tagKey: string, label: string, elementType: ElementType): TemplateElement {
  const isText = elementType === 'text';
  return {
    id: generateId(),
    tagKey,
    label,
    elementType,
    ...(isText ? { content: '' } : {}),
    x: 10,
    y: 10,
    width:  elementType === 'barcode'        ? 60
          : elementType === 'qrcode'          ? 30
          : elementType === 'table'           ? 80
          : elementType === 'barcode-table'   ? 120
          : elementType === 'divider'         ? 80
          : 60,
    height: elementType === 'barcode'        ? 20
          : elementType === 'qrcode'          ? 30
          : elementType === 'image'           ? 30
          : elementType === 'table'           ? 40
          : elementType === 'barcode-table'   ? 50
          : elementType === 'divider'         ? 2
          : elementType === 'header'          ? 10
          : 8,
    showLabel: false,
    //!isText && elementType !== 'barcode' && elementType !== 'qrcode'
    //                   && elementType !== 'image'   && elementType !== 'divider',
    fontSize: elementType === 'header' ? 14 : 10,
    fontWeight: elementType === 'header' ? 'bold' : 'normal',
    fontStyle: 'normal',
    textAlign: 'left',
    color: '#000000',
    backgroundColor: 'transparent',
    zIndex: 1,
  };
}

export function generateId(): string {
  return Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
}
